import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Search, Link2, Unlink, FileText, User, Eye, ChevronLeft, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPlayerManagement() {
  const [players, setPlayers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [linkingId, setLinkingId] = useState(null);
  const [athleteUsers, setAthleteUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/players?search=${encodeURIComponent(search)}&page=${page}&limit=20`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setPlayers(data.players);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (err) {
      toast.error("Failed to load players");
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  const fetchUsers = async (q = "") => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/athlete-users?search=${encodeURIComponent(q)}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setAthleteUsers(data.users);
      }
    } catch (err) {
      console.error("Failed to fetch users");
    }
  };

  const startLinking = (athleteId) => {
    setLinkingId(athleteId);
    fetchUsers();
  };

  const linkPlayer = async (athleteId, userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/player/${athleteId}/link`, {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setLinkingId(null);
        fetchPlayers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Link failed");
      }
    } catch (err) {
      toast.error("Link failed");
    }
  };

  const unlinkPlayer = async (athleteId) => {
    if (!window.confirm("Unlink this player from their user account?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/player/${athleteId}/unlink`, {
        method: "POST",
        headers,
      });
      if (res.ok) {
        toast.success("Player unlinked");
        fetchPlayers();
      }
    } catch (err) {
      toast.error("Unlink failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-player-management">
      {/* Search bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search players by name, school, or ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-900 focus:border-transparent"
            data-testid="player-search-input"
          />
        </div>
        <div className="text-sm text-gray-500">{total} players</div>
      </div>

      {/* Player list */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading players...</div>
      ) : players.length === 0 ? (
        <div className="text-center py-8 text-gray-400">No players found</div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Player</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">School</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600">Position</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600">Report</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600">Account</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.athlete_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`player-row-${p.athlete_id}`}>
                  <td className="py-3 px-4">
                    <div className="font-semibold text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.athlete_id}</div>
                  </td>
                  <td className="py-3 px-4 text-gray-700">{p.school || "--"}</td>
                  <td className="py-3 px-4 text-center">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {p.position_primary || p.position || "--"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {p.has_scouting_report ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                        <FileText className="h-3 w-3" /> PDF
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">None</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {p.has_user_account ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium" title={p.linked_user_email}>
                        <User className="h-3 w-3" /> Linked
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                        Unlinked
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <a
                        href={`/athlete/${p.athlete_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 text-gray-500 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                        title="View Profile"
                      >
                        <Eye className="h-4 w-4" />
                      </a>
                      {p.has_user_account ? (
                        <button
                          onClick={() => unlinkPlayer(p.athlete_id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Unlink Account"
                        >
                          <Unlink className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => startLinking(p.athlete_id)}
                          className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Link to Account"
                          data-testid={`link-btn-${p.athlete_id}`}
                        >
                          <Link2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-gray-500">Page {page} of {pages}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages}
            onClick={() => setPage(p => p + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Link modal */}
      {linkingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="link-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Link to User Account</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select an athlete account to link this scouting report to. If the player already has a profile under that account, the data will be merged.
            </p>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  fetchUsers(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                data-testid="user-search-input"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {athleteUsers.length === 0 ? (
                <p className="text-center text-gray-400 py-4 text-sm">No athlete accounts found</p>
              ) : (
                athleteUsers.map((u) => (
                  <div
                    key={u.user_id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div>
                      <div className="font-semibold text-sm text-gray-900">{u.name}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                      {u.has_player_profile && (
                        <div className="text-xs text-blue-600 mt-0.5">Already has a profile — data will be merged</div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => linkPlayer(linkingId, u.user_id)}
                      className="bg-blue-900 text-white hover:bg-blue-800"
                      data-testid={`select-user-${u.user_id}`}
                    >
                      <Link2 className="h-3.5 w-3.5 mr-1" />
                      {u.has_player_profile ? "Merge" : "Link"}
                    </Button>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => { setLinkingId(null); setUserSearch(""); }}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
